import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import Setup from './pages/Setup';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Loads from './pages/Loads';
import SearchResults from './pages/SearchResults';
import LoadDetail from './pages/LoadDetail';
import LoadWorkspace from './pages/LoadWorkspace';
import Carriers from './pages/Carriers';
import CarrierDetail from './pages/CarrierDetail';
import Facilities from './pages/Facilities';
import FacilityDetail from './pages/FacilityDetail';
import Reports from './pages/Reports';
import ReloadDatabase from './pages/ReloadDatabase';
import Interactions from './pages/Interactions';
import Workflows from './pages/Workflows';
import Settings from './pages/Settings';
import '@/App.css';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[var(--tms-bg)]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[var(--tms-primary)] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-[var(--tms-text-secondary)]">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  // If allowedRoles is specified, check if user has access
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect carrier and customer users to loads page if they try to access restricted pages
    if (user.role === 'carrier' || user.role === 'customer') {
      return <Navigate to="/loads" />;
    }
    return <Navigate to="/dashboard" />;
  }
  
  return children;
};

// Component to handle default route based on user role
const DefaultRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[var(--tms-bg)]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[var(--tms-primary)] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-[var(--tms-text-secondary)]">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  // Carrier and Customer users go to loads, everyone else goes to dashboard
  if (user.role === 'carrier' || user.role === 'customer') {
    return <Navigate to="/loads" />;
  }
  
  return <Navigate to="/dashboard" />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/setup" element={<Setup />} />
            <Route path="/register" element={<Register />} />
            <Route path="/" element={<DefaultRedirect />} />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'account_manager', 'operator', 'support_operator', 'customer']}>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/customers" 
              element={
                <ProtectedRoute>
                  <Customers />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/customers/:id" 
              element={
                <ProtectedRoute>
                  <CustomerDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/loads" 
              element={
                <ProtectedRoute>
                  <Loads />
                </ProtectedRoute>
              } 
            />
            <Route path="/search" element={<ProtectedRoute><SearchResults /></ProtectedRoute>} />
            <Route 
              path="/loads/new" 
              element={
                <ProtectedRoute>
                  <LoadDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/loads/:id" 
              element={
                <ProtectedRoute>
                  <LoadWorkspace />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/carriers" 
              element={
                <ProtectedRoute>
                  <Carriers />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/carriers/:id" 
              element={
                <ProtectedRoute>
                  <CarrierDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/facilities" 
              element={
                <ProtectedRoute>
                  <Facilities />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/facilities/:id" 
              element={
                <ProtectedRoute>
                  <FacilityDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/reports" 
              element={
                <ProtectedRoute>
                  <Reports />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/reload" 
              element={
                <ProtectedRoute>
                  <ReloadDatabase />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/interactions" 
              element={
                <ProtectedRoute>
                  <Interactions />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/workflows" 
              element={
                <ProtectedRoute>
                  <Workflows />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              } 
            />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
